// src/components/ReferralEmailInvite.jsx
import { useState } from "react";
import {
  Box,
  Text,
  Input,
  Button,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { supabase } from "../utils/supabaseClient";

/**
 * ReferralEmailInvite
 * Sends referral invites via email.
 * - Inserts into campaign_emails
 * - Inserts into referrals (referrer_id = integer businessId)
 */
export default function ReferralEmailInvite({ referrerId }) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const toast = useToast();

  const inviteLink = `https://postcode.coupons/signup?ref=${referrerId}`;

  const handleSend = async () => {
    if (!email || !email.includes("@")) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid business email address.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSending(true);

    try {
      // 1. Track campaign email
      const { error: emailError } = await supabase
        .from("campaign_emails")
        .insert({
          recipient_email: email,
          flyer_id: null,
          status: "pending",
        });

      if (emailError) throw emailError;

      // 2. Track referral
      const { error: referralError } = await supabase
        .from("referrals")
        .insert({
          referrer_id: referrerId, // integer PK
          referred_email: email,
          credited: false,
          referral_source: "email_invite",
        });

      if (referralError) throw referralError;

      toast({
        title: "Invite sent!",
        description: `Referral email sent to ${email}`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setEmail("");
    } catch (err) {
      console.error("Referral invite error:", err);
      toast({
        title: "Error sending invite",
        description: "Something went wrong while sending the referral invite.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Box mt={6}>
      <Text fontWeight="bold" mb={2}>
        📧 Send Referral Invite
      </Text>

      <VStack spacing={2} align="start">
        <Input
          placeholder="Enter business email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          size="sm"
          isDisabled={sending}
        />

        <Button
          size="sm"
          onClick={handleSend}
          colorScheme="blue"
          isLoading={sending}
          loadingText="Sending..."
        >
          Send Invite
        </Button>
      </VStack>
    </Box>
  );
}
